import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import Container from "@/components/Shared/Container";
import { navData, type MetroAccent } from "@/components/Navigation/navData";

export const METRO_ACCENT: Record<MetroAccent, { bar: string; text: string }> = {
  red: { bar: "bg-metro-red-600", text: "text-metro-red-500" },
  orange: { bar: "bg-metro-orange-500", text: "text-metro-orange-400" },
  yellow: { bar: "bg-metro-yellow-400", text: "text-metro-yellow-400" },
  green: { bar: "bg-metro-green-500", text: "text-metro-green-400" },
  blue: { bar: "bg-metro-blue-500", text: "text-metro-blue-400" },
};

interface ServicesMegaMenuProps {
  language: "en" | "es";
}

export const ServicesMegaMenu: React.FC<ServicesMegaMenuProps> = ({ language }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  const rootRef = useRef<HTMLLIElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const services = navData[language].services;
  const isActive = services.activePrefixes.some((prefix) =>
    location.pathname.startsWith(prefix)
  );

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Every close path must go through here — a pending open timer left behind
  // would reopen the panel right after a link click navigates.
  const closeNow = useCallback(() => {
    clearTimers();
    setOpen(false);
  }, [clearTimers]);

  const handlePointerEnter = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (open || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      setOpen(true);
    }, 80);
  };

  const handlePointerLeave = (e: React.PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    // 300ms bridges the trigger→panel hover gap without feeling sticky.
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
    }, 300);
  };

  const handleClick = () => {
    clearTimers();
    setOpen((prev) => !prev);
  };

  const focusFirstOnOpenRef = useRef(false);

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "ArrowDown" && !open) {
      e.preventDefault();
      clearTimers();
      focusFirstOnOpenRef.current = true;
      setOpen(true);
    }
  };

  // Focus the first link after the open render commits — a timer here races
  // the AnimatePresence mount and intermittently loses.
  useEffect(() => {
    if (!open || !focusFirstOnOpenRef.current) return;
    focusFirstOnOpenRef.current = false;
    const firstLink = document.querySelector<HTMLAnchorElement>(
      "#services-mega-panel a[href]"
    );
    firstLink?.focus();
  }, [open]);

  const handleBlurCapture = (e: React.FocusEvent<HTMLLIElement>) => {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) {
      closeNow();
    }
  };

  useEffect(() => {
    closeNow();
  }, [location.pathname, closeNow]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeNow();
        triggerRef.current?.focus();
      }
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        closeNow();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [open, closeNow]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  const gridVariants = {
    hidden: {},
    show: {
      transition: reduceMotion
        ? {}
        : {
            staggerChildren: 0.03,
            delayChildren: 0.04,
          },
    },
  };

  const columnVariants = {
    hidden: reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0.12 : 0.35,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  return (
    <li ref={rootRef} className="relative" onBlurCapture={handleBlurCapture}>
      <button
        ref={triggerRef}
        type="button"
        className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors duration-200 rounded-lg ${
          isActive ? "text-metro-orange-500" : "text-dark-300 hover:text-dark-50"
        }`}
        onClick={handleClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        onKeyDown={handleTriggerKeyDown}
        aria-expanded={open}
        aria-controls="services-mega-panel"
      >
        {services.trigger}
        <m.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.2 }}
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 8.25l-7.5 7.5-7.5-7.5"
            />
          </svg>
        </m.span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <m.div
              className="fixed inset-x-0 top-[88px] h-screen bg-dark-950/60 backdrop-blur-[2px] hidden xl:block"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0.1 : 0.2 }}
              aria-hidden="true"
              onClick={closeNow}
              onPointerEnter={(e) => {
                if (e.pointerType !== "mouse") return;
                clearTimers();
                closeTimerRef.current = setTimeout(() => setOpen(false), 100);
              }}
            />

            <m.div
              id="services-mega-panel"
              aria-label={services.panelLabel}
              onPointerEnter={handlePointerEnter}
              onPointerLeave={handlePointerLeave}
              className="fixed inset-x-0 top-[88px] hidden xl:block bg-dark-900/[0.98] backdrop-blur-xl border-t border-dark-800 shadow-2xl shadow-black/50 max-h-[calc(100vh-120px)] overflow-y-auto"
              initial={{ opacity: 0, y: reduceMotion ? 0 : -14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: reduceMotion ? 0 : -8,
                transition: { duration: reduceMotion ? 0.12 : 0.16 },
              }}
              transition={{
                duration: reduceMotion ? 0.12 : 0.24,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              <Container size="xl">
                <m.div
                  className="grid grid-cols-12 gap-x-10 gap-y-8 py-10"
                  variants={gridVariants}
                  initial="hidden"
                  animate="show"
                >
                  {services.groups.map((group) => (
                    <m.div
                      key={group.heading}
                      className="col-span-3"
                      variants={columnVariants}
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <span
                          className={
                            "h-[3px] w-5 rounded-full " +
                            METRO_ACCENT[group.accent].bar
                          }
                          aria-hidden="true"
                        />
                        <span
                          className={
                            "text-[11px] font-semibold uppercase tracking-[0.18em] " +
                            METRO_ACCENT[group.accent].text
                          }
                        >
                          {group.heading}
                        </span>
                      </div>
                      <ul className="space-y-1">
                        {group.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              onClick={closeNow}
                              className="group relative block rounded-lg px-3 py-2 -mx-3 transition-colors hover:bg-dark-800/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                            >
                              <span
                                className={
                                  "absolute left-0 top-2.5 bottom-2.5 w-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity " +
                                  METRO_ACCENT[group.accent].bar
                                }
                                aria-hidden="true"
                              />
                              <span className="block text-[15px] font-semibold text-dark-100 group-hover:text-white transition-colors">
                                {link.label}
                              </span>
                              {link.description && (
                                <span className="mt-0.5 block text-[13px] leading-snug text-dark-400">
                                  {link.description}
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </m.div>
                  ))}

                  <m.div className="col-span-3" variants={columnVariants}>
                    <div className="flex items-center gap-2 mb-4">
                      <span
                        className={
                          "h-[3px] w-5 rounded-full " +
                          METRO_ACCENT[services.industries.accent].bar
                        }
                        aria-hidden="true"
                      />
                      <span
                        className={
                          "text-[11px] font-semibold uppercase tracking-[0.18em] " +
                          METRO_ACCENT[services.industries.accent].text
                        }
                      >
                        {services.industries.heading}
                      </span>
                    </div>

                    <ul className="space-y-0.5">
                      {services.industries.links.map((link) => (
                        <li key={link.to}>
                          <Link
                            to={link.to}
                            onClick={closeNow}
                            className="block rounded-lg px-3 py-1.5 -mx-3 text-sm font-medium text-dark-200 transition-colors hover:bg-dark-800/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-metro-orange-500"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={services.industries.viewAll.to}
                      onClick={closeNow}
                      className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-metro-yellow-400 hover:text-metro-yellow-300 transition-colors"
                    >
                      {services.industries.viewAll.label}
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                        />
                      </svg>
                    </Link>

                    <div className="mt-6 rounded-xl border border-dark-700 bg-dark-800/50 p-5">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-metro-orange-500">
                        {services.featured.eyebrow}
                      </p>
                      <Link
                        to={services.featured.cta.to}
                        onClick={closeNow}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-metro-orange-500 px-4 py-2.5 text-sm font-semibold text-dark-950 shadow-lg shadow-metro-orange-500/20 transition-colors hover:bg-metro-orange-400 active:bg-metro-orange-600"
                      >
                        {services.featured.cta.label}
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                          />
                        </svg>
                      </Link>

                      <div className="mt-4 space-y-1">
                        {services.featured.secondary.map((secLink) => (
                          <Link
                            key={secLink.to}
                            to={secLink.to}
                            onClick={closeNow}
                            className="flex items-center gap-1.5 text-sm text-dark-300 transition-colors hover:text-metro-orange-400"
                          >
                            <span>{secLink.label}</span>
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                              />
                            </svg>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </m.div>
                </m.div>
              </Container>
              <div className="metro-stripe" aria-hidden="true" />
            </m.div>
          </>
        )}
      </AnimatePresence>
    </li>
  );
};

export default ServicesMegaMenu;
